import { useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { DefaultPeriodOptionsColumn } from "Constants";
import { format } from "date-fns";
import { Company } from "Models/company";
import { CostingReportCSV, OrdersManagement } from "Models/report.orders.management";
import { Searches } from "Models/shipeezi.table";
import { CompanyConnectionsService } from "Services/company.connections.service";
import OrdersManagementReportService from "Services/orders.management.report.service";

import { useAppState } from "../../../../../../../context/StateContext";
import { Branch } from "Models/branch";
import { uniqueArrayWithComparator } from "Util/uniqueArray";
import { UsedByStatuses } from "Util/enums";
import StatusesService from "Services/statuses.service";
import JobTypeService from "Services/job.type.service";
import { ReportManagement } from "Util/queries";
import { Cronjob } from "Models/cronjob";
import ReportManagementService from "Services/analytics.report.service";
import { AxiosError } from "axios";
import { checkError } from "Util/checkError";
import { useTranslation } from "react-i18next";
import { handleGeneratePdf } from "Util/generateReport";
import { GenerateJob } from "Models/generate.job";

interface ReportFilters {
  [x: string]: string[] | number[];
}

export const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [filters, setFilters] = useState<ReportFilters>({
    companyId: [String(currentUser?.companyId!)],
  });
  const [search, setSearch] = useState<Searches>([]);
  const [period, setPeriod] = useState<DefaultPeriodOptionsColumn>(
    DefaultPeriodOptionsColumn.CUSTOM_PERIOD
  );
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const defaultEndDate = new Date();

  const [companies, setCompanies] = useState<Company[]>();
  const [total, setTotal] = useState<number>(0);
  const [ordersManagementsReports, setOrdersManagementsReports] = useState<OrdersManagement[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [orderId, setOrderId] = useState<number>();
  const [startDate, setStartDate] = useState<Date | undefined>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);
  const [openRows, setOpenRows] = useState<number[]>([]);

  const clearOpenRows = () => {
    setOpenRows([]);
  };

  const handleSearch = (value: Searches) => {
    setSearch(value);
    setPage(0);
  };

  useQuery({
    queryKey: ["companies-with-connection", currentUser?.companyId],
    queryFn: async () => {
      return CompanyConnectionsService.getCompaniesWithConnection().then(({ data }) => {
        setCompanies(data);

        return data;
      });
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
    initialData() {
      return [];
    },
  });

  const ordersManagementsFilters = useMemo(() => {
    const companyIds = filters?.companyId?.length
      ? filters?.companyId
      : companies?.map((company) => company.id!);

    return {
      searchByOr: search && search.length > 0 ? JSON.stringify(search) : "",
      startDate: startDate && format(startDate, "yyyy-MM-dd"),
      endDate: endDate && format(endDate, "yyyy-MM-dd"),
      ...filters,
      hasTasks:
        filters?.hasTasks?.length === 1 ? (filters?.hasTasks as string[])?.includes("1") : {},
      companyId: JSON.stringify(companyIds),
      ...(filters?.status ? { status: JSON.stringify(filters.status) } : {}),
    };
  }, [filters, companies, search, startDate, endDate]);

  const enableGridAndWidgetQuerys = useMemo(() => {
    return !!(ordersManagementsFilters?.companyId?.length && startDate && endDate);
  }, [ordersManagementsFilters?.companyId, startDate, endDate]);

  const branchesList = useMemo(() => {
    if (currentUser?.branches?.length) {
      const branches = currentUser?.branches?.sort((branchA, branchB) =>
        (branchA?.name || "").localeCompare(branchB?.name || "")
      );

      const uniqueBranches = uniqueArrayWithComparator(branches, (a, b) => a.id === b.id);

      return uniqueBranches;
    }

    return [] as Branch[];
  }, [currentUser]);

  const getOrdersManagements = () =>
    useQuery(
      ["orders.management.reports.list", page, pageSize, ordersManagementsFilters],
      () =>
        OrdersManagementReportService.getOrdersManagementReports({
          ...ordersManagementsFilters,
          page,
          pageSize,
        }),
      {
        onSuccess: (response) => {
          if (response && response?.data) {
            setTotal(response.data.count);
            setOrdersManagementsReports(response.data.orders);
            clearOpenRows();
          }
        },
        enabled: enableGridAndWidgetQuerys,
      }
    );

  const useGetJobStatus = () =>
    useQuery({
      queryKey: ["statuses.list"],
      queryFn: async () => {
        const response = await StatusesService.getAll({
          usedBy: UsedByStatuses.ORDERS,
          companyId: currentUser?.companyId,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
    });

  const useGetJobType = () =>
    useQuery({
      queryKey: ["jobType.list"],
      queryFn: async () => {
        const response = await JobTypeService.getAll({
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
    });

  const useExportOrderCSVData = (callback: (data: CostingReportCSV[]) => void) => {
    return useMutation({
      mutationKey: ["export.management.order.costing.csv", ordersManagementsFilters],
      mutationFn: async () => {
        return OrdersManagementReportService.getOrdersCostingExportCSVData({
          ...ordersManagementsFilters,
        });
      },
      onSuccess: (response) => {
        if (response?.data?.length) {
          callback(response.data);
        }
      },
    });
  };

  const useExportSingleOrderCSVData = (callback: (data: CostingReportCSV[]) => void) => {
    return useMutation({
      mutationKey: ["export.management.order.costing.csv", ordersManagementsFilters],
      mutationFn: async () => {
        return OrdersManagementReportService.getOrdersCostingExportCSVData({
          ...ordersManagementsFilters,
          id: orderId,
        });
      },
      onSuccess: (response) => {
        setOrderId(undefined);
        if (response?.data?.length) {
          callback(response.data);
        }
      },
      onError: (error: AxiosError<Error>) => {
        setOrderId(undefined);
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    });
  };

  const useCreateEmailSubscription = (
    onSuccessCallback: () => void,
    onErrorCallback: (errorMessage?: string) => void
  ) => {
    return useMutation({
      mutationKey: ReportManagement.createSubscription(),
      mutationFn: (variables: Cronjob) =>
        ReportManagementService.createManagement(variables, { ...filters, startDate, endDate }),
      onSuccess: () => {
        onSuccessCallback();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          onErrorCallback(error?.response?.data?.message);
        }
      },
    });
  };

  const useGeneratePDFView = () => {
    return useMutation({
      mutationKey: ["orders.management.report.pdf.view", ordersManagementsFilters],
      mutationFn: (generate: GenerateJob) =>
        OrdersManagementReportService.generateOrdersCostingReport(generate),
      onSuccess: (response) => {
        setOrderId(undefined);
        if (response?.data && response?.data?.content) {
          handleGeneratePdf({
            data: response.data,
            setOpenModal: undefined,
            exportFile: true,
          });
        }
      },
      onError: (error: AxiosError<Error>) => {
        setOrderId(undefined);
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    });
  };

  return {
    branchesList,
    companies,
    defaultEndDate,
    defaultStartDate,
    endDate,
    filters,
    getOrdersManagements,
    handleSearch,
    openRows,
    orderId,
    ordersManagementsReports,
    page,
    pageSize,
    period,
    setEndDate,
    setFilters,
    setOpenRows,
    setOrderId,
    setPage,
    setPageSize,
    setPeriod,
    setStartDate,
    startDate,
    total,
    useCreateEmailSubscription,
    useExportOrderCSVData,
    useExportSingleOrderCSVData,
    useGeneratePDFView,
    useGetJobStatus,
    useGetJobType,
  };
};
